import React from 'react';
import { motion } from 'motion/react';
import { Sun, Moon, Sparkles } from 'lucide-react';
import { useSaaSContext } from '../../store/SaaSContext';
import { playfulAudio } from '../../utils/playfulAudio';

interface ThemeToggleProps {
  className?: string;
  showLabel?: boolean;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ className = '', showLabel = false }) => {
  const { theme, toggleTheme } = useSaaSContext();

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    playfulAudio.playCutePop(theme === 'dark' ? 1.2 : theme === 'light' ? 1.4 : 0.9);
    toggleTheme();
  };

  const isLight = theme === 'light';
  const isGlass = theme === 'glassmorphism';

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border transition-all cursor-pointer select-none group shadow-xs ${
        isGlass
          ? 'bg-purple-600/25 hover:bg-purple-600/35 text-purple-200 border-purple-400/50 shadow-[0_0_15px_rgba(168,85,247,0.35)] backdrop-blur-md'
          : isLight
          ? 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 border-amber-300/80 shadow-amber-500/5'
          : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border-zinc-700 shadow-black/20'
      } ${className}`}
      title={
        isGlass
          ? 'Currently Aurora Glassmorphism (Click for Dark Mode)'
          : isLight
          ? 'Currently Light Mode (Click for Aurora Glassmorphism)'
          : 'Currently Dark Mode (Click for Light Mode)'
      }
      aria-label="Toggle theme mode"
    >
      <motion.div
        key={theme}
        initial={{ rotate: -45, scale: 0.7, opacity: 0 }}
        animate={{ rotate: 0, scale: 1, opacity: 1 }}
        exit={{ rotate: 45, scale: 0.7, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        className="flex items-center justify-center shrink-0"
      >
        {isGlass ? (
          <Sparkles className="w-3.5 h-3.5 text-pink-300 group-hover:rotate-45 transition-transform duration-300 animate-pulse" />
        ) : isLight ? (
          <Sun className="w-3.5 h-3.5 text-amber-500 group-hover:rotate-45 transition-transform duration-300" />
        ) : (
          <Moon className="w-3.5 h-3.5 text-indigo-400 group-hover:-rotate-12 transition-transform duration-300" />
        )}
      </motion.div>

      {showLabel && (
        <span className="text-xs font-semibold tracking-wide">
          {isGlass ? 'Glassmorphism' : isLight ? 'Light Mode' : 'Dark Mode'}
        </span>
      )}
    </button>
  );
};
